/**
 * Fetch / state logic for the Catalog Issues tab.
 *
 * Per the frontend design doc this tab keeps its state in LOCAL component
 * state (React hooks), not Redux. A degraded catalog dependency never fails
 * the HTTP call - the BFF always answers 200 with a `dependencyStatus`
 * field - so callers must branch on `dependencyStatus`, and only a genuine
 * transport error puts these hooks into the `failed` state.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCatalogIssues, searchCatalogIssues } from "../../app/api";
import {
  AsinWithCatalogIssues,
  CatalogIssueDetailResult,
  CatalogIssuesResult,
} from "../../../from-server/asin-central/catalog-issues";
import { isNoFilterApplied } from "../../searchSideBar/queryStringHelpers";
import logger from "../../../art-logger-config";

export type CatalogIssuesFetchStatus = "idle" | "loading" | "loaded" | "failed";

export interface CatalogIssuesSearchState {
  readonly status: CatalogIssuesFetchStatus;
  readonly result?: CatalogIssuesResult;
}

export interface CatalogIssueDetailState {
  readonly status: CatalogIssuesFetchStatus;
  /** ASIN the detail pane is showing (set as soon as a row is selected). */
  readonly asin?: string;
  readonly marketplace?: string;
  readonly result?: CatalogIssueDetailResult;
}

/**
 * Client-side filter over the loaded rows: case-insensitive substring match
 * on ASIN or description. Does NOT re-query the backend.
 */
export function filterAsinsWithIssues(
  rows: readonly AsinWithCatalogIssues[],
  filterText: string
): AsinWithCatalogIssues[] {
  const needle = filterText.trim().toLowerCase();
  if (needle === "") {
    return [...rows];
  }
  return rows.filter(
    (row) =>
      row.asin.toLowerCase().includes(needle) ||
      row.description.toLowerCase().includes(needle)
  );
}

/**
 * Loads the catalog-issues search result whenever the submitted global
 * filter (`queryString`) changes. Skips the sentinel "no filter applied"
 * values. Errors are contained here so a failure in this tab can never
 * break the rest of the Validation page.
 */
export function useCatalogIssuesSearch(
  queryString: string | undefined,
  mockState?: string
): CatalogIssuesSearchState {
  const [state, setState] = useState<CatalogIssuesSearchState>({
    status: "idle",
  });

  useEffect(() => {
    if (isNoFilterApplied(queryString)) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    searchCatalogIssues(queryString as string, mockState)
      .then((result) => {
        if (!cancelled) {
          setState({ status: "loaded", result: result });
        }
      })
      .catch((error: unknown) => {
        logger.error(`Failed to load catalog issues: ${String(error)}`);
        if (!cancelled) {
          setState({ status: "failed" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [queryString, mockState]);

  return state;
}

export interface CatalogIssueDetailsHook {
  /** Detail state for each selected ASIN, in selection order. */
  readonly details: ReadonlyArray<CatalogIssueDetailState>;
  /**
   * Reconcile the shown details to the current table selection: fetch the
   * detail for newly-selected ASINs (appended in selection order) and drop
   * the detail for deselected ones. Safe to call on every selection change.
   */
  readonly setSelection: (items: readonly AsinWithCatalogIssues[]) => void;
  /** Clear all selected-issue details (e.g. when the search query changes). */
  readonly reset: () => void;
}

function detailKey(asin: string, marketplace: string): string {
  return `${asin}/${marketplace}`;
}

export function useCatalogIssueDetails(): CatalogIssueDetailsHook {
  // Detail state keyed by `${asin}/${marketplace}`, plus the selection order
  // so blocks render in the order the user checked them.
  const [byKey, setByKey] = useState<
    ReadonlyMap<string, CatalogIssueDetailState>
  >(() => new Map());
  const [order, setOrder] = useState<ReadonlyArray<string>>([]);
  // Per-key monotonic request id: a slow response is applied only if its key
  // is still selected and no newer fetch for that key has superseded it.
  const requestIdsRef = useRef<Map<string, number>>(new Map());

  const fetchDetail = useCallback(
    (key: string, asin: string, marketplace: string) => {
      const requestId = (requestIdsRef.current.get(key) ?? 0) + 1;
      requestIdsRef.current.set(key, requestId);
      getCatalogIssues(asin, marketplace)
        .then((result) => {
          if (requestIdsRef.current.get(key) !== requestId) {
            return;
          }
          setByKey((prev) => {
            if (!prev.has(key)) {
              return prev; // deselected while the request was in flight
            }
            const next = new Map(prev);
            next.set(key, {
              status: "loaded",
              asin: asin,
              marketplace: marketplace,
              result: result,
            });
            return next;
          });
        })
        .catch((error: unknown) => {
          logger.error(
            `Failed to load catalog issue detail for ${asin}/${marketplace}: ${String(
              error
            )}`
          );
          if (requestIdsRef.current.get(key) !== requestId) {
            return;
          }
          setByKey((prev) => {
            if (!prev.has(key)) {
              return prev;
            }
            const next = new Map(prev);
            next.set(key, {
              status: "failed",
              asin: asin,
              marketplace: marketplace,
            });
            return next;
          });
        });
    },
    []
  );

  const setSelection = useCallback(
    (items: readonly AsinWithCatalogIssues[]) => {
      const selected = items.map((item) => ({
        key: detailKey(item.asin, item.obfuscatedMarketplaceId),
        asin: item.asin,
        marketplace: item.obfuscatedMarketplaceId,
      }));
      const selectedKeys = new Set(selected.map((s) => s.key));

      // Forget request ids for deselected keys so a re-selection re-fetches.
      for (const key of Array.from(requestIdsRef.current.keys())) {
        if (!selectedKeys.has(key)) {
          requestIdsRef.current.delete(key);
        }
      }

      setOrder(selected.map((s) => s.key));
      setByKey((prev) => {
        const next = new Map<string, CatalogIssueDetailState>();
        for (const s of selected) {
          next.set(
            s.key,
            prev.get(s.key) ?? {
              status: "loading",
              asin: s.asin,
              marketplace: s.marketplace,
            }
          );
        }
        return next;
      });

      // Fetch only newly-selected keys (those without an in-flight/known id).
      for (const s of selected) {
        if (!requestIdsRef.current.has(s.key)) {
          fetchDetail(s.key, s.asin, s.marketplace);
        }
      }
    },
    [fetchDetail]
  );

  const reset = useCallback(() => {
    requestIdsRef.current.clear();
    setOrder([]);
    setByKey(new Map());
  }, []);

  const details = order
    .map((key) => byKey.get(key))
    .filter((state): state is CatalogIssueDetailState => state !== undefined);

  return { details: details, setSelection: setSelection, reset: reset };
}
